import { Component, HostBinding, input, OnInit } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { Bic } from '@app/core/payment/types/bic';
import { Iban } from '@app/core/payment/types/iban';
import { SortCode } from '@app/core/payment/types/sort-code';
import { nextFieldsetsWithIban } from '@app/next/forms/features/next-form-known-fields';
import {
  CursorPositionUpdateFactory,
  FmntsMaskedInputModule,
  ValueTransform,
} from '@fmnts/components/masked-input';
import { isString } from '@fmnts/core';
import {
  AbstractDeclarativeInputFieldComponent,
  FormTextInputField,
} from '@fmnts/declarative-forms';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';

/**
 * Component that renders an input field.
 */
@Component({
  selector: 'next-text-field',
  templateUrl: './text-field.component.html',
  standalone: true,
  imports: [SharedDeclarativeCampaignFormsModule, FmntsMaskedInputModule],
})
export class TextFieldComponent
  extends AbstractDeclarativeInputFieldComponent<
    FormTextInputField,
    string | null
  >
  implements ControlValueAccessor, OnInit
{
  @HostBinding('class') componentClass = 'form-field form-field__text';

  /**
   * Value for the `autocomplete` attribute of the input field.
   */
  readonly autocomplete = input(this._getAutocompleteBehavior());

  protected _displayValueTransformer?: ValueTransform = undefined;
  private _value: string | null = '';

  ngOnInit() {
    // TODO: Pass transformers as @Input from generic-field.component
    if (nextFieldsetsWithIban.includes(this.fieldset.key)) {
      if (this.field.key === 'account_bic_or_bankcode') {
        this._displayValueTransformer = {
          transform: (value) => new Bic(value || '').value.toUpperCase(),
          reverse: (displayValue) =>
            new Bic(displayValue || '').value.toUpperCase(),
        };
      }

      if (this.field.key === 'account_iban_or_number') {
        this._displayValueTransformer = {
          transform: (value) => new Iban(value || '').format().toUpperCase(),
          reverse: (displayValue) =>
            Iban.parse(displayValue || '').value.toUpperCase(),
          updateCursor: CursorPositionUpdateFactory.fixedGroups(1, 4),
        };
      }
    }

    if (this.fieldset.key === 'sortCodesBankData') {
      if (this.field.key === 'account_bic_or_bankcode') {
        this._displayValueTransformer = {
          transform: (value) =>
            new SortCode(value || '').format().toUpperCase(),
          reverse: (displayValue) =>
            SortCode.parse(displayValue || '').value.toUpperCase(),
          updateCursor: CursorPositionUpdateFactory.fixedGroups(1, 2),
        };
      }
    }

    if (this.field.widgetOptions.format === 'alphanumeric') {
      this._displayValueTransformer = {
        transform: (v) => v || '',
        reverse: (displayValue) =>
          typeof displayValue === 'string'
            ? displayValue.replace(/[^a-zA-Z0-9]/g, '')
            : displayValue,
      };
    }

    if (this.field.widgetOptions.format === 'alphabetical') {
      this._displayValueTransformer = {
        transform: (v) => v || '',
        reverse: (displayValue) =>
          typeof displayValue === 'string'
            ? displayValue.replace(/[^a-zA-Z]/g, '')
            : displayValue,
      };
    }
  }

  /**
   * Trims white space before and after the value
   * Used on first and last name to improve data quality
   */
  public trim() {
    if (this._value === null) {
      return;
    }

    if (this.fieldset.key === 'personalDataKeys') {
      if (this.field.key === 'first_name' || this.field.key === 'last_name') {
        this.value = this._value.trim();
      }
    }
  }

  get value() {
    return this._value;
  }

  set value(value) {
    this.changeValue(value);
    this._onTouched();
  }

  /**
   * Changes the value and calls the onChange listeners.
   *
   * @param value The new value
   */
  private changeValue(value: string | null) {
    this._value = value;
    this._onChange(value);
  }

  writeValue(value: unknown) {
    // Set value to given string or null. It might be changed afterwards in case that
    // a reset was detected.
    this._value = isString(value) ? value : null;
  }

  get inputMode(): string {
    return ['email', 'number', 'tel'].includes(
      this.field.widgetOptions.keyboardType,
    )
      ? this.field.widgetOptions.keyboardType
      : 'text';
  }

  get inputType(): string {
    if (this.field.widgetOptions.format === 'date') {
      return 'date';
    }

    return ['email', 'number', 'tel'].includes(
      this.field.widgetOptions.keyboardType,
    )
      ? this.field.widgetOptions.keyboardType
      : 'text';
  }

  get autocapitalize(): string {
    return this.field.widgetOptions.autocapitalize;
  }

  get showError(): boolean {
    return (
      this.form && (this.value || this.control.touched) && this.control.invalid
    );
  }
}
